import { produce } from "immer";
import { ensureNavbarBlocks } from "../constants/navbarDefaults";
import { migrateHtmlModeContent, migrateHtmlPageOnlyToHybrid } from "../constants/htmlPageBlock";
import { WebsitePageContent } from "../interfaces/WebsiteBlock";
import { WebsiteFormOption, WebsitePage } from "../interfaces/WebsitePage";
import {
  deleteWebsitePageSuccessAction,
  getWebsiteFormsSuccessAction,
  getWebsitePageFailureAction,
  getWebsitePageInitAction,
  getWebsitePageSuccessAction,
  getWebsitePagesSuccessAction,
  postWebsitePageSuccessAction,
  putWebsitePageSuccessAction,
  setEditorContentAction,
  setEditorViewModeAction,
  setSelectedBlockAction,
  WebsitePageApiAction,
} from "./api-actions";

export interface WebsitePageState {
  pages: WebsitePage[];
  page: WebsitePage | null;
  pageLoadFailed: boolean;
  forms: WebsiteFormOption[];
  editorContent: WebsitePageContent;
  selectedBlockId: string | null;
  viewMode: "desktop" | "tablet" | "mobile";
}

const initialState: WebsitePageState = {
  pages: [],
  page: null,
  pageLoadFailed: false,
  forms: [],
  editorContent: { blocks: ensureNavbarBlocks([]) },
  selectedBlockId: null,
  viewMode: "desktop",
};

const normalizeEditorContent = (content: WebsitePageContent, pageName?: string) => {
  const migrated = migrateHtmlModeContent(content);
  const hybrid = migrateHtmlPageOnlyToHybrid(migrated, pageName);
  return {
    ...hybrid,
    blocks: ensureNavbarBlocks(hybrid.blocks || [], pageName),
  };
};

export default function websitePageReducer(
  state = initialState,
  action: WebsitePageApiAction
): WebsitePageState {
  return produce(state, (draft) => {
    switch (action.type) {
      case "[WebsitePage] Get Pages Success":
        draft.pages = action.payload.pages;
        break;
      case "[WebsitePage] Get Page Init":
        draft.pageLoadFailed = false;
        break;
      case "[WebsitePage] Get Page Success":
        draft.page = action.payload.page;
        draft.pageLoadFailed = false;
        draft.editorContent = normalizeEditorContent(
          action.payload.page.contentJson || { blocks: [] },
          action.payload.page.name
        );
        break;
      case "[WebsitePage] Get Page Failure":
        draft.pageLoadFailed = true;
        break;
      case "[WebsitePage] Post Page Success":
        draft.pages.push(action.payload.page);
        draft.page = action.payload.page;
        draft.editorContent = normalizeEditorContent(
          action.payload.page.contentJson || { blocks: [] },
          action.payload.page.name
        );
        break;
      case "[WebsitePage] Put Page Success":
        draft.page = action.payload.page;
        draft.editorContent = normalizeEditorContent(
          action.payload.page.contentJson || { blocks: [] },
          action.payload.page.name
        );
        draft.pages = draft.pages.map((p) =>
          p.id === action.payload.page.id ? action.payload.page : p
        );
        break;
      case "[WebsitePage] Delete Page Success":
        draft.pages = draft.pages.filter((p) => p.id !== action.payload.pageId);
        if (draft.page?.id === action.payload.pageId) {
          draft.page = null;
        }
        break;
      case "[WebsitePage] Get Forms Success":
        draft.forms = action.payload.forms;
        break;
      case "[WebsitePage] Set Editor Content":
        draft.editorContent = normalizeEditorContent(action.payload.contentJson);
        break;
      case "[WebsitePage] Set Selected Block":
        draft.selectedBlockId = action.payload.blockId;
        break;
      case "[WebsitePage] Set View Mode":
        draft.viewMode = action.payload.viewMode;
        break;
      default:
        break;
    }
  });
}
